import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Input, TextField } from '@heroui/react';
import { Button } from '@/components/Button';
import { Tooltip } from '@/components/Tooltip';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';
import { errToToast } from '@/lib/humanizeApiError';
import { stripMarkdown } from '@/lib/markdown';
import { parseForwarded } from '@bunker/shared/forwarded';
import { formatTime } from '@/lib/format';
import { emitAppEvent } from '@/lib/appEvents';
import { useVaultShelves } from '@/lib/useVaultShelves';
import { LIMITS } from '@bunker/shared/limits';
import {
  ChevronLeftIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  FolderIcon,
  PlusIcon,
  TrashIcon,
  VaultIcon,
} from '@/components/icons';
import type { Message, VaultShelf } from '@/types/api';

/**
 * Vault command-center — the header button for the personal Сейф. Web parity
 * with the mobile VaultInfo/VaultShelf screens, folded into one popover:
 *   • "О Сейфе" blurb,
 *   • shelves ("полки") list with inline create / rename / delete,
 *   • drill into a shelf to see its filed items (click → jump to message).
 * The server already backs all of this; this is purely the missing web UI.
 */
export function VaultPanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Only fetch while the popover is open — and re-fetch on each open so counts
  // stay fresh after the shelf picker (a separate hook instance) mutated them.
  // Surface optimistic create/rename/remove failures instead of letting the
  // shelf silently snap back (the hook reconciles the list but stays quiet).
  const onShelfError = useCallback(
    (err: unknown) => toast.error(...errToToast(err, t('shelves.saveError'))),
    [t],
  );
  const { shelves, loading, error, reload, create, rename, remove } = useVaultShelves(open, onShelfError);
  // null = the shelves list; a shelf = its item view.
  const [active, setActive] = useState<VaultShelf | null>(null);

  return (
    <Popover
      isOpen={open}
      onOpenChange={(v) => {
        setOpen(v);
        // Reset to the list synchronously on close (no derived-state effect).
        if (!v) setActive(null);
      }}
    >
      <Tooltip label={t('shelves.panelTitle')}>
        <Popover.Trigger
          aria-label={t('shelves.panelTitle')}
          className="group rounded-lg p-2 text-ink-dim transition-colors hover:bg-white/10 hover:text-accent"
        >
          <FolderIcon className="h-[22px] w-[22px]" />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-96 max-w-[calc(100vw-1.5rem)]" placement="bottom end">
        <Popover.Dialog className="overflow-hidden p-0">
          {active ? (
            <ShelfItemsView shelf={active} onBack={() => setActive(null)} onClose={() => setOpen(false)} />
          ) : (
            <ShelvesListView
              shelves={shelves}
              loading={loading}
              error={error}
              onRetry={reload}
              onOpen={setActive}
              onCreate={create}
              onRename={rename}
              onRemove={remove}
            />
          )}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

function ShelvesListView(props: {
  shelves: VaultShelf[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onOpen: (shelf: VaultShelf) => void;
  onCreate: (name: string) => Promise<VaultShelf | null>;
  onRename: (id: string, name: string) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const atLimit = props.shelves.length >= LIMITS.vaultShelf.perUser;

  async function create() {
    const name = draft.trim();
    if (!name || busy || atLimit) return;
    setBusy(true);
    await props.onCreate(name);
    setBusy(false);
    setDraft('');
  }

  return (
    <>
      <header className="flex items-center gap-2 border-b border-bg-raised/40 px-4 py-3">
        <span className="grid h-7 w-7 place-items-center rounded-lg bg-primary text-white">
          <VaultIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-ink">{t('shelves.panelTitle')}</div>
        </div>
      </header>

      {/* Create */}
      <div className="flex items-end gap-2 border-b border-bg-raised/40 p-2">
        <TextField fullWidth value={draft} onChange={setDraft} isDisabled={atLimit}>
          <Input
            variant="secondary"
            placeholder={atLimit ? t('shelves.limitReached') : t('shelves.namePlaceholder')}
            maxLength={LIMITS.vaultShelf.nameMax}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void create();
              }
            }}
          />
        </TextField>
        <Button variant="ghost" size="sm" onClick={create} disabled={!draft.trim() || atLimit} loading={busy} aria-label={t('shelves.create')}>
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="scrollbar-thin max-h-[55vh] overflow-y-auto py-1">
        {props.loading && props.shelves.length === 0 && (
          <p className="px-4 py-6 text-center text-xs text-ink-muted">{t('common.loading')}</p>
        )}
        {!props.loading && props.shelves.length === 0 && props.error && (
          <div className="flex flex-col items-center gap-2 px-4 py-6 text-center">
            <p className="text-xs leading-relaxed text-ink-muted">{t('shelves.loadListError')}</p>
            <Button variant="ghost" size="sm" onClick={props.onRetry}>
              {t('shelves.retry')}
            </Button>
          </div>
        )}
        {!props.loading && props.shelves.length === 0 && !props.error && (
          <p className="px-4 py-6 text-center text-xs leading-relaxed text-ink-muted">{t('shelves.emptyList')}</p>
        )}
        {props.shelves.map((shelf) =>
          editingId === shelf.id ? (
            <div key={shelf.id} className="flex items-center gap-1.5 px-2 py-1.5">
              <TextField fullWidth value={editName} onChange={setEditName} aria-label={t('common.rename')}>
                <Input
                  autoFocus
                  variant="secondary"
                  maxLength={LIMITS.vaultShelf.nameMax}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (editName.trim()) void props.onRename(shelf.id, editName);
                      setEditingId(null);
                    } else if (e.key === 'Escape') {
                      setEditingId(null);
                    }
                  }}
                />
              </TextField>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t('common.rename')}
                onClick={() => {
                  if (editName.trim()) void props.onRename(shelf.id, editName);
                  setEditingId(null);
                }}
              >
                <CheckIcon className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" aria-label={t('common.cancel')} onClick={() => setEditingId(null)}>
                <CloseIcon className="h-4 w-4" />
              </Button>
            </div>
          ) : confirmId === shelf.id ? (
            <div key={shelf.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <span className="truncate text-xs text-danger">{t('shelves.deleteConfirm', { name: shelf.name })}</span>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    void props.onRemove(shelf.id);
                    setConfirmId(null);
                  }}
                >
                  {t('common.delete')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmId(null)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div
              key={shelf.id}
              className="group/shelf mx-1 flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-white/8"
            >
              <button
                type="button"
                onClick={() => props.onOpen(shelf)}
                className="flex min-w-0 flex-1 items-center gap-3 text-left"
              >
                <FolderIcon className="h-4 w-4 shrink-0 text-ink-muted group-hover/shelf:text-accent" />
                <span className="flex-1 truncate text-sm text-ink">{shelf.name}</span>
                <span className="shrink-0 text-xs tabular-nums text-ink-muted">{shelf.count}</span>
              </button>
              <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/shelf:opacity-100 group-focus-within/shelf:opacity-100 [@media(pointer:coarse)]:opacity-100">
                <button
                  type="button"
                  aria-label={t('common.rename')}
                  onClick={() => {
                    setEditName(shelf.name);
                    setEditingId(shelf.id);
                  }}
                  className="rounded-md p-1 text-ink-muted hover:bg-white/10 hover:text-accent"
                >
                  <EditIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={t('common.delete')}
                  onClick={() => setConfirmId(shelf.id)}
                  className="rounded-md p-1 text-ink-muted hover:bg-danger/15 hover:text-danger"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ),
        )}
      </div>
    </>
  );
}

function ShelfItemsView(props: { shelf: VaultShelf; onBack: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [items, setItems] = useState<Message[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(false);
    api
      .listShelfItems(props.shelf.id)
      .then((res) => {
        if (!cancelled) setItems(res.messages);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [props.shelf.id]);

  return (
    <>
      <header className="flex items-center gap-2 border-b border-bg-raised/40 px-2 py-2">
        <button
          type="button"
          aria-label={t('common.back')}
          onClick={props.onBack}
          className="rounded-lg p-1.5 text-ink-muted hover:bg-white/10 hover:text-accent"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <FolderIcon className="h-4 w-4 shrink-0 text-accent" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">{props.shelf.name}</span>
      </header>
      <div className="scrollbar-thin max-h-[60vh] overflow-y-auto">
        {!items && !error && (
          <p className="px-4 py-6 text-center text-xs text-ink-muted">{t('common.loading')}</p>
        )}
        {error && <p className="px-4 py-3 text-xs text-danger-ink">{t('shelves.itemsError')}</p>}
        {items && items.length === 0 && (
          <p className="px-4 py-6 text-center text-xs text-ink-muted">{t('shelves.shelfEmpty')}</p>
        )}
        {items?.map((m) => {
          // Saved-from-someone messages are stored in the forward wire-format.
          // Show the ORIGINAL author + clean body (like the vault feed), not the
          // raw "↪ Переслано · …" string.
          const fwd = parseForwarded(m.content);
          const author = fwd ? fwd.authorName : m.author.displayName || m.author.username;
          const body = (fwd ? fwd.quotedBody : stripMarkdown(m.content)).trim();
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => {
                emitAppEvent('chat:scroll-to-message', { messageId: m.id });
                props.onClose();
              }}
              className="block w-full px-4 py-3 text-left transition-colors hover:bg-accent/10"
              style={{ borderBottom: '1px solid rgb(var(--ring-inset) / var(--ring-inset-alpha))' }}
            >
              <header className="mb-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-light tracking-[-0.015em] text-accent">{author}</span>
                <span className="shrink-0 text-[10px] text-ink-muted">{formatTime(new Date(m.createdAt))}</span>
              </header>
              <p className="line-clamp-3 text-xs leading-snug text-ink">
                {body || t('shelves.attachmentOnly')}
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
}
